import React, { useState, useEffect } from "react";
import { Text } from "@mantine/core";
import TagBadge, { tagBadgeColor, tagName } from "./TagBadge";

const manualTagOrder = [
  "like",
  "veryGood",
  "good",
  "finished",
  "bad",
  "inProgress",
  "todo",
];
const manualTagOrderIndex = manualTagOrder.reduce((acc, name, index) => {
  acc[name] = index;
  return acc;
}, {});

// Default view: personal recommendation (2), global selectable (3),
// global kind (4). "Show all tags" reveals custom / genre / subCategory /
// year — the auto-derived families sit behind collapsible [Жанр] /
// [Время] / [Свои тэги] triggers; subCategory (film/series) is only two
// chips so it renders inline without a group.
const alwaysVisibleTypes = new Set([2, 3, 4]);

const SelectableTags = ({ relevantTags, alreadySelectedTags = [], onSelectionChange, language }) => {
  const [selectedTags, setSelectedTags] = useState([]);
  const [showAllTags, setShowAllTags] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => new Set());

  useEffect(() => {
    setSelectedTags(alreadySelectedTags);
  }, [alreadySelectedTags]);

  const toggleTagSelection = (selectedTag) => {
    let updatedSelection;
    if (selectedTags.find((t) => t.tag.id === selectedTag.tag.id)) {
      updatedSelection = selectedTags.filter((t) => t.tag.id !== selectedTag.tag.id);
    } else {
      updatedSelection = [...selectedTags, selectedTag];
    }
    setSelectedTags(updatedSelection);
    onSelectionChange(updatedSelection);
  };

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectedTagIds = new Set(selectedTags.map((tagObject) => tagObject.tag.id));

  const sortByManualThenBackend = (arr) => [...arr].sort((a, b) => {
    const aIsManual = a.tag.type === 3 ? 0 : 1;
    const bIsManual = b.tag.type === 3 ? 0 : 1;
    if (aIsManual !== bIsManual) return aIsManual - bIsManual;
    if (a.tag.type === 3 && b.tag.type === 3) {
      const aIndex = manualTagOrderIndex[a.tag.name] ?? 999;
      const bIndex = manualTagOrderIndex[b.tag.name] ?? 999;
      return aIndex - bIndex;
    }
    return 0;
  });

  // Main list: always-visible types + any selected chip (regardless of type
  // — a selected genre/year chip stays visible when its group is collapsed).
  const mainList = sortByManualThenBackend(
    relevantTags.filter((t) => alwaysVisibleTypes.has(t.tag.type) || selectedTagIds.has(t.tag.id))
  );

  // Behind "Show all tags": each family, minus anything already in mainList.
  const notInMain = (t) => !alwaysVisibleTypes.has(t.tag.type) && !selectedTagIds.has(t.tag.id);
  const subCategoryExtras = relevantTags.filter((t) => t.tag.type === 6 && notInMain(t));
  const genreExtras       = relevantTags.filter((t) => t.tag.type === 5 && notInMain(t));
  const yearExtras        = relevantTags.filter((t) => t.tag.type === 7 && notInMain(t));
  const customExtras      = relevantTags.filter((t) => t.tag.type === 1 && notInMain(t));

  const hasGenreGroup = relevantTags.some((t) => t.tag.type === 5);
  const hasYearGroup  = relevantTags.some((t) => t.tag.type === 7);
  const anyGenreSelected = selectedTags.some((t) => t.tag.type === 5);
  const anyYearSelected  = selectedTags.some((t) => t.tag.type === 7);

  const hasAdditionalTags = relevantTags.some(
    (tagObject) => !alwaysVisibleTypes.has(tagObject.tag.type)
  );

  const renderTagChip = (tagObject) => {
    const { name } = tagObject.tag;
    const { count } = tagObject;
    const { type } = tagObject.tag;
    const isSelected = selectedTags.find((t) => t.tag.id === tagObject.tag.id);
    return (
      <TagBadge
        key={tagObject.tag.id}
        component="button"
        type="button"
        className="cursor-pointer flex-shrink-0"
        radius="md"
        size="md"
        color={tagBadgeColor(name, type)}
        variant="filled"
        style={isSelected
          ? { transform: 'scale(1.08)', boxShadow: '0 0 0 2px var(--mantine-color-body), 0 0 0 4px var(--mantine-color-text)', transition: 'transform 0.15s, box-shadow 0.15s' }
          : { transition: 'transform 0.15s' }}
        onClick={() => toggleTagSelection(tagObject)}
      >
        {tagObject.tag.imgUrl !== "" ? (
          <div className="flex items-center text-center max-h-6 gap-1">
            <span>#</span>
            <img className="w-5 max-w-none h-5 rounded-full" src={tagObject.tag.imgUrl} />
            <span>{tagName(name, language)}</span>
            <Text span size="xs" c="white" style={{ opacity: 0.85 }}>({count})</Text>
          </div>
        ) : (
          <>
            {tagName(name, language)}&nbsp;
            <Text span size="xs" c="white" style={{ opacity: 0.85 }}>({count})</Text>
          </>
        )}
      </TagBadge>
    );
  };

  const renderGroupTrigger = (key, color, labelRu, labelEn) => {
    const open = openGroups.has(key);
    return (
      <TagBadge
        key={`group:${key}`}
        component="button"
        type="button"
        radius="md"
        size="md"
        variant={open ? "filled" : "light"}
        color={color}
        className="cursor-pointer flex-shrink-0"
        onClick={() => toggleGroup(key)}
      >
        {(language === "ru" ? labelRu : labelEn) + " " + (open ? "▲" : "▼")}
      </TagBadge>
    );
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 py-2">
        {mainList.map(renderTagChip)}
        {!showAllTags && hasAdditionalTags && (
          <TagBadge
            type="button"
            component="button"
            radius="md"
            size="md"
            variant="filled"
            color="blue"
            className="cursor-pointer flex-shrink-0"
            onClick={() => setShowAllTags(true)}
          >
            {language === "ru" ? "Показать все тэги" : "Show all tags"}
          </TagBadge>
        )}
        {showAllTags && (
          <>
            {subCategoryExtras.map(renderTagChip)}
            {hasGenreGroup && !anyGenreSelected && renderGroupTrigger("genre", "indigo", "Жанр", "Genre")}
            {openGroups.has("genre") && !anyGenreSelected && genreExtras.map(renderTagChip)}
            {hasYearGroup && !anyYearSelected && renderGroupTrigger("year", "lime", "Время", "Time")}
            {openGroups.has("year") && !anyYearSelected && yearExtras.map(renderTagChip)}
            {customExtras.map(renderTagChip)}
            <TagBadge
              type="button"
              component="button"
              radius="md"
              size="md"
              variant="filled"
              color="blue"
              className="cursor-pointer flex-shrink-0"
              onClick={() => setShowAllTags(false)}
            >
              {language === "ru" ? "Скрыть" : "Hide"}
            </TagBadge>
          </>
        )}
      </div>
    </div>
  );
};

export default SelectableTags;
